import { useLocation } from 'react-router-dom';
import { categories, comics, faqs } from '@/mocks/comics';

interface SEOSchemaProps {
  type: 'home' | 'category' | 'article' | 'about';
  categorySlug?: string;
  articleId?: number;
}

export default function SEOSchema({ type, categorySlug, articleId }: SEOSchemaProps) {
  const location = useLocation();
  const baseUrl = 'https://neoom.cn';
  const currentUrl = `${baseUrl}${location.pathname}`;

  const getOrganizationSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫屋',
    url: baseUrl,
    description: '韩漫免费看，下拉式漫画大全，韩国漫画在线。在韩漫屋享受韩漫免费看的乐趣，下拉式漫画大全与友好的韩国漫画在线界面，为您营造温馨舒适的阅读环境。',
    sameAs: [
      'https://weibo.com/hanmanwu',
      'https://space.bilibili.com/hanmanwu',
    ],
  });

  const getWebsiteSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '韩漫屋',
    url: baseUrl,
    description: '韩漫免费看，下拉式漫画大全，韩国漫画在线',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${baseUrl}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  });

  const getWebPageSchema = (name: string, description: string) => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url: currentUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫屋',
      url: baseUrl,
    },
  });

  const getBreadcrumbSchema = (items: { name: string; url: string }[]) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  });

  const getFAQSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  });

  const getArticleSchema = () => {
    const comic = comics.find((c) => c.id === articleId);
    if (!comic) return null;
    const cat = categories.find((c) => c.slug === comic.category);
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: `${comic.title} - 韩漫屋韩国漫画在线`,
      description: comic.description,
      author: {
        '@type': 'Person',
        name: comic.author,
      },
      datePublished: comic.updateDate,
      dateModified: comic.updateDate,
      publisher: {
        '@type': 'Organization',
        name: '韩漫屋',
        url: baseUrl,
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': currentUrl,
      },
      about: {
        '@type': 'Thing',
        name: cat?.name || comic.category,
      },
    };
  };

  const getSchemas = () => {
    const schemas: object[] = [getOrganizationSchema(), getWebsiteSchema()];

    switch (type) {
      case 'home':
        schemas.push(
          getWebPageSchema(
            '韩漫屋 - 韩漫免费看 | 下拉式漫画大全 | 韩国漫画在线',
            '在韩漫屋享受韩漫免费看的乐趣，下拉式漫画大全与友好的韩国漫画在线界面，为您营造温馨舒适的阅读环境。海量韩国漫画免费阅读。'
          )
        );
        schemas.push(
          getBreadcrumbSchema([{ name: '首页', url: currentUrl }])
        );
        schemas.push(getFAQSchema());
        break;

      case 'category': {
        const cat = categories.find((c) => c.slug === categorySlug);
        const catName = cat?.name || categorySlug;
        schemas.push(
          getWebPageSchema(
            `${catName} - 韩漫屋韩国漫画在线`,
            `韩漫屋${catName}分类，收录热门韩国漫画，下拉式阅读，免费在线看漫画。`
          )
        );
        schemas.push(
          getBreadcrumbSchema([
            { name: '首页', url: baseUrl },
            { name: catName, url: currentUrl },
          ])
        );
        break;
      }

      case 'article': {
        const article = getArticleSchema();
        if (article) schemas.push(article);
        const comic = comics.find((c) => c.id === articleId);
        const cat = categories.find((c) => c.slug === comic?.category);
        schemas.push(
          getBreadcrumbSchema([
            { name: '首页', url: baseUrl },
            { name: cat?.name || '分类', url: `${baseUrl}/category/${comic?.category}` },
            { name: comic?.title || '详情', url: currentUrl },
          ])
        );
        break;
      }

      case 'about':
        schemas.push(
          getWebPageSchema(
            '关于我们 - 韩漫屋',
            '了解韩漫屋的品牌故事、发展历程与口碑评价，获取我们的联系方式。韩漫免费看，下拉式漫画大全。'
          )
        );
        schemas.push(
          getBreadcrumbSchema([
            { name: '首页', url: baseUrl },
            { name: '关于我们', url: currentUrl },
          ])
        );
        break;
    }

    return schemas;
  };

  const schemas = getSchemas();

  return (
    <>
      {schemas.map((schema, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema, null, 0) }}
        />
      ))}
    </>
  );
}